import React, { useEffect, useState } from "react";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { CtaBlock } from "@/components/CtaBlock";
import { QuickLeadForm } from "@/components/forms/QuickLeadForm";
import { api } from "@/lib/api";

/** Reusable SEO landing template used by product / mobility / medical cluster pages. */
export default function SeoLandingPage({ data, crumbs, category, showDisclaimer, showSpecs }) {
  const [content, setContent] = useState(null);
  useEffect(() => {
    if (showSpecs || showDisclaimer) api.get(showSpecs ? "/content/product" : "/content/regulatory").then((r) => setContent(r.data)).catch(() => {});
  }, [showSpecs, showDisclaimer]);

  const faqJsonLd = data.faq?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: data.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  } : null;

  return (
    <>
      <Seo title={data.title} description={data.answer} keywords={data.keyword} noindex={data.draft} jsonLd={faqJsonLd} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Breadcrumbs items={crumbs} />
      </div>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <span className="uppercase text-xs font-bold tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">{category}</span>
        <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>{data.h1}</h1>
        <div className="mt-5 sc-prose">
          <div className="in-breve"><strong>In breve</strong><p>{data.answer}</p></div>
          <p>{data.intro}</p>
          {data.sections?.map((sec, i) => (
            <div key={i}>
              <h2>{sec.h}</h2>
              <div dangerouslySetInnerHTML={{ __html: sec.p }} />
            </div>
          ))}

          {showSpecs && content?.specs && (
            <>
              <h2>Specifiche tecniche</h2>
              <table className="w-full border border-slate-200 rounded-lg overflow-hidden text-base">
                <tbody>
                  {Object.entries(content.specs).map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-2.5 px-4 font-semibold text-slate-700 bg-slate-50 w-1/2">{k}</td>
                      <td className="py-2.5 px-4 text-slate-600">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {content.last_updated && <p className="text-sm text-slate-500 mt-3">Ultimo aggiornamento: {new Date(content.last_updated).toLocaleDateString("it-IT")}</p>}
            </>
          )}

          {data.faq?.length > 0 && (
            <>
              <h2>Domande frequenti</h2>
              {data.faq.map(([q, a], i) => (<div key={i}><h3>{q}</h3><p>{a}</p></div>))}
            </>
          )}
        </div>

        {showDisclaimer && content && (
          <div className="mt-8 rounded-xl border-l-4 p-5 text-sm text-[#6E0A2E]" style={{ background: "#FDE7EF", borderColor: "#EA155B" }} data-testid="regulatory-disclaimer">
            <p className="font-bold uppercase tracking-wide text-xs mb-1">Nota informativa</p>
            <p>{content.disclaimer}</p>
            {content.last_updated && <p className="mt-2 font-medium">Ultimo aggiornamento: {new Date(content.last_updated).toLocaleDateString("it-IT")} · Revisione: {content.reviewer}</p>}
          </div>
        )}
      </article>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-10 grid md:grid-cols-2 gap-6 items-start">
        <QuickLeadForm compact />
        <div className="md:pt-2">
          <CtaBlock variant={data.cta || "test"} />
        </div>
      </div>
      <div className="h-16" />
    </>
  );
}
